/**
 * Payment Pages - Success and Cancel pages for Stripe checkout
 * Supports: event registration, school fees, donations, book purchases
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { paymentAPI, authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Loader2, CheckCircle2, XCircle, Clock,
  GraduationCap, Heart, BookOpen, CalendarCheck, ArrowLeft
} from 'lucide-react';

const TYPE_CONFIG = {
  fee: {
    icon: GraduationCap,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    successTitle: 'Fee Payment Successful!',
    successDescription: 'Your school fee has been recorded.',
    cancelTitle: 'Fee Payment Cancelled',
    cancelDescription: 'Your fee payment was not completed. You can pay again from the school portal.',
    returnLabel: 'Back to School Portal',
    returnPath: '/login',
  },
  donation: {
    icon: Heart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    successTitle: 'Thank You for Your Donation!',
    successDescription: 'Your generous contribution has been received.',
    cancelTitle: 'Donation Cancelled',
    cancelDescription: 'Your donation was not completed. You can try again anytime.',
    returnLabel: 'Back to Donation Portal',
    returnPath: '/donate-portal',
  },
  book: {
    icon: BookOpen,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    successTitle: 'Book Purchase Complete!',
    successDescription: 'You now have access to your book.',
    cancelTitle: 'Purchase Cancelled',
    cancelDescription: 'Your book purchase was not completed. The book is still available.',
    returnLabel: 'Back to Bookstore',
    returnPath: '/shop',
  },
  default: {
    icon: CalendarCheck,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    successTitle: 'Payment Successful!',
    successDescription: 'Your registration is now confirmed.',
    cancelTitle: 'Payment Cancelled',
    cancelDescription: 'Your payment was cancelled. Your registration is saved but pending payment.',
    returnLabel: 'Return Home',
    returnPath: '/',
  },
};

function getConfig(type) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.default;
}

// ============== Payment Success Page ==============
export const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type') || 'default';
  const [status, setStatus] = useState('verifying');
  const [paymentData, setPaymentData] = useState(null);

  const config = getConfig(paymentType);
  const Icon = config.icon;

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await paymentAPI.getStatus(sessionId);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);

          // For event registrations, auto-login delegate
          if (!paymentType || paymentType === 'default') {
            const pendingPayment = sessionStorage.getItem('pending_payment');
            if (pendingPayment) {
              try {
                const { access_token, event_id } = JSON.parse(pendingPayment);
                localStorage.setItem('eventflow_token', access_token);
                const profile = await authAPI.getDelegateProfile();
                useAuthStore.getState().setAuth(access_token, profile.data, "delegate", event_id);
                sessionStorage.removeItem('pending_payment');
                toast.success("Payment successful! Welcome to the event.");
                setTimeout(() => navigate('/delegate'), 3000);
              } catch (loginErr) {
                console.error('Auto-login after payment failed:', loginErr);
              }
            }
          }
        } else {
          setStatus('pending');
        }
      } catch (err) {
        console.error('Payment verification failed:', err);
        setStatus('error');
      }
    };

    verifyPayment();
  }, [sessionId, navigate, paymentType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="w-full max-w-md text-center shadow-lg" data-testid="payment-success-card">
        {status === 'verifying' && (
          <CardHeader>
            <Loader2 className="h-16 w-16 animate-spin text-slate-400 mx-auto mb-4" />
            <CardTitle>Verifying Payment</CardTitle>
            <CardDescription>Please wait while we confirm your payment...</CardDescription>
          </CardHeader>
        )}

        {status === 'success' && (
          <>
            <CardHeader>
              <div className={`h-20 w-20 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-4`}>
                <CheckCircle2 className={`h-12 w-12 ${config.color}`} />
              </div>
              <CardTitle className="text-2xl">{config.successTitle}</CardTitle>
              <CardDescription className="text-base mt-1">
                {config.successDescription}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentData?.amount != null && (
                <div className={`${config.bgColor} rounded-xl p-4`}>
                  <p className="text-sm text-slate-500">Amount Paid</p>
                  <p className={`text-2xl font-bold ${config.color}`}>
                    ${paymentData.amount?.toFixed(2)} <span className="text-sm font-normal text-slate-400 uppercase">{paymentData.currency || 'usd'}</span>
                  </p>
                </div>
              )}
              {paymentType === 'default' && (
                <p className="text-sm text-slate-500">Redirecting to your delegate portal...</p>
              )}
              <Button
                onClick={() => navigate(config.returnPath)}
                className="w-full"
                data-testid="payment-success-return-btn"
              >
                {config.returnLabel}
              </Button>
            </CardContent>
          </>
        )}

        {status === 'pending' && (
          <>
            <CardHeader>
              <Clock className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <CardTitle>Payment Processing</CardTitle>
              <CardDescription>Your payment is being processed. Please check back shortly.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" onClick={() => window.location.reload()} className="w-full" data-testid="check-again-btn">
                Check Again
              </Button>
              <Button variant="ghost" onClick={() => navigate(config.returnPath)} className="w-full text-slate-500">
                {config.returnLabel}
              </Button>
            </CardContent>
          </>
        )}

        {status === 'error' && (
          <>
            <CardHeader>
              <XCircle className="h-16 w-16 text-rose-400 mx-auto mb-4" />
              <CardTitle>Verification Failed</CardTitle>
              <CardDescription>We could not verify your payment. Please contact support if you were charged.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={() => navigate(config.returnPath)} variant="outline" className="w-full" data-testid="payment-error-return-btn">
                {config.returnLabel}
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

// ============== Payment Cancel Page ==============
export const PaymentCancelPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentType = searchParams.get('type') || 'default';
  const eventId = searchParams.get('event_id');

  const config = getConfig(paymentType);
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="w-full max-w-md text-center shadow-lg" data-testid="payment-cancel-card">
        <CardHeader>
          <div className="h-20 w-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Icon className="h-10 w-10 text-slate-400" />
          </div>
          <CardTitle>{config.cancelTitle}</CardTitle>
          <CardDescription className="mt-1">
            {config.cancelDescription}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={() => navigate(eventId ? `/event/${eventId}` : config.returnPath)}
            className="w-full"
            data-testid="payment-cancel-return-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {eventId ? 'Back to Event' : config.returnLabel}
          </Button>
          {paymentType === 'default' && (
            <Button variant="ghost" onClick={() => navigate('/login')} className="w-full text-slate-500">
              Login to Complete Later
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default { PaymentSuccessPage, PaymentCancelPage };
